// src/features/signature/VerifyLookup.tsx
// Look up a target (document/order/task/...) and show who signed it and the
// evidence on record — the read-only "verify" surface, open to any
// authenticated user (not admin-gated).
// See docs/ux/signature-ia.md section 1.

import { Fragment, useState } from 'react';
import { useI18n, useSignature } from '@react/hooks';
import { Button, Input, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import type { Party, Signature } from '@core/types/signature';
import { enumSuffix, partyStatusVariant, signatureStatusVariant } from './logic';

export function VerifyLookup() {
  const { t } = useI18n();
  const store = useSignature();
  const [targetType, setTargetType] = useState('document');
  const [targetId, setTargetId] = useState('');
  const [parties, setParties] = useState<Party[] | null>(null);
  const [signatures, setSignatures] = useState<Signature[] | null>(null);
  const [expanded, setExpanded] = useState<Signature | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetType.trim() || !targetId.trim()) return;
    setIsLoading(true);
    try {
      const [p, s] = await Promise.all([
        store.ListPartiesByTarget(targetType.trim(), targetId.trim()),
        store.ListSignaturesByTarget(targetType.trim(), targetId.trim()),
      ]);
      setParties(p.items ?? []);
      setSignatures(s.items ?? []);
      setExpanded(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpand = async (sig: Signature) => {
    if (expanded?.entity?.id === sig.entity?.id) {
      setExpanded(null);
      return;
    }
    if (!sig.entity?.id) return;
    setIsLoadingEvidence(true);
    try {
      const r = await store.GetSignature(sig.entity.id);
      setExpanded(r);
    } finally {
      setIsLoadingEvidence(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t('signature.verify.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('signature.verify.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <Input label={t('signature.verify.targetType')} value={targetType} onChange={(e) => setTargetType(e.target.value)} className="w-40" required />
        <Input label={t('signature.verify.targetId')} value={targetId} onChange={(e) => setTargetId(e.target.value)} className="w-64" required />
        <Button type="submit" size="sm" disabled={isLoading}>{isLoading ? t('common.loading') : t('signature.verify.lookup')}</Button>
      </form>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {parties && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">{t('signature.verify.partiesTitle')}</h2>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('signature.field.role')}</TableHead>
                <TableHead>{t('signature.field.displayName')}</TableHead>
                <TableHead>{t('signature.field.status')}</TableHead>
                <TableHead>{t('signature.field.signOrder')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parties.length === 0 ? (
                <TableEmpty colSpan={4} message={t('signature.verify.noParties')} />
              ) : (
                parties.map((p) => (
                  <TableRow key={p.entity?.id}>
                    <TableCell>{t(`signature.role.${enumSuffix(p.role, 'PARTY_ROLE_')}`)}</TableCell>
                    <TableCell>{p.display_name || p.external_email || '—'}</TableCell>
                    <TableCell><StatusBadge variant={partyStatusVariant(p.status)} label={t(`signature.partyStatus.${enumSuffix(p.status, 'PARTY_STATUS_')}`)} size="sm" /></TableCell>
                    <TableCell>{p.sign_order}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {signatures && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">{t('signature.verify.signaturesTitle')}</h2>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('signature.field.signer')}</TableHead>
                <TableHead>{t('signature.field.method')}</TableHead>
                <TableHead>{t('signature.field.status')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signatures.length === 0 ? (
                <TableEmpty colSpan={4} message={t('signature.verify.noSignatures')} />
              ) : (
                signatures.map((s) => (
                  <Fragment key={s.entity?.id}>
                    <TableRow>
                      <TableCell>{s.signer?.display_name || '—'}</TableCell>
                      <TableCell>{t(`signature.method.${enumSuffix(s.method, 'SIGNATURE_METHOD_')}`)}</TableCell>
                      <TableCell><StatusBadge variant={signatureStatusVariant(s.status)} label={t(`signature.status.${enumSuffix(s.status, 'SIGNATURE_STATUS_')}`)} size="sm" /></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" disabled={isLoadingEvidence} onClick={() => handleExpand(s)}>
                          {expanded?.entity?.id === s.entity?.id ? t('signature.verify.hideEvidence') : t('signature.verify.viewEvidence')}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded?.entity?.id === s.entity?.id && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <pre className="max-h-72 overflow-auto rounded-md bg-muted/40 p-3 text-xs">{JSON.stringify(expanded, null, 2)}</pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
